import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import aboutusImage from "../../tools/industryimages/aboutimage1.webp";
import { trackEvent } from "../../firebase/analytics";
import { useEffect } from "react";
import "../../styles/AboutUs.css";

import {
  Users,
  Clock,
  BarChart3,
  Target,
  Eye,
  ShieldCheck,
  Zap,
  Heart,
} from "lucide-react";

const AboutUs = () => {
  const navigate = useNavigate();
  useEffect(() => {
    trackEvent("about_page_view", {
      page: "about",
    });
  }, []);
  return (
    <>
      <Navbar />

      <main className="about-page" role="main">
        {/* HERO */}
        <section className="about-hero">
          <div className="about-hero-copy">
            <span className="about-eyebrow">About Us</span>

            <h1>
              We built Oiiko for teams that <span>keep the world running.</span>
            </h1>

            <p>
              Oiiko is an all-in-one workforce management platform that helps
              employers simplify operations, support their teams, and grow with
              confidence.
            </p>

            <div className="about-proof-row">
              <div className="about-proof-item">
                <Users size={24} />
                <p>
                  Built for <br /> Every Team
                </p>
              </div>

              <div className="about-proof-item">
                <Clock size={24} />
                <p>
                  Save hours <br /> Every Week
                </p>
              </div>

              <div className="about-proof-item">
                <BarChart3 size={24} />
                <p>
                  One Platform. <br /> Everything You Need.
                </p>
              </div>
            </div>
          </div>

          <div className="about-hero-visual">
            <img
              src={aboutusImage}
              alt="Oiiko team"
              className="about-team-photo"
            />
          </div>
        </section>

        {/* STORY + MISSION/VISION */}
        <section className="about-story-section">
          <div className="about-story-copy">
            <span className="about-eyebrow">Our Story</span>
            <h2>Why we created Oiiko</h2>
            <div className="about-small-line" />

            <p>
              We saw how hard it was for businesses to manage people, time,
              schedules, and daily operations using multiple tools that didn’t
              communicate.
            </p>

            <p>
              So we created Oiiko — one simple platform that brings everything
              together, saves time, and helps teams focus on what really
              matters.
            </p>

            <div className="about-handwritten">
              Simple for managers. <br /> Powerful for teams.
            </div>
          </div>

          <div className="about-mission-card">
            <div className="about-mission-item">
              <div>
                <h3>Our Mission</h3>
                <p>
                  We empower teams with the tools to manage employees, track
                  performance, schedule work, approve requests, train staff, and
                  make data-driven decisions without the clutter of multiple
                  apps or manual processes.
                </p>
              </div>
              <span className="about-icon-circle">
                <Target size={28} />
              </span>
            </div>

            <hr />

            <div className="about-mission-item">
              <div>
                <h3>Our Vision</h3>
                <p>
                  To make managing people simple again, one platform for every
                  part of the employee journey.
                </p>
              </div>
              <span className="about-icon-circle">
                <Eye size={28} />
              </span>
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section className="about-values-section">
          <span className="about-eyebrow center">Our Values</span>
          <h2>The values that drive us</h2>
          <div className="about-small-line center-line" />

          <div className="values-grid">
            <div className="value-card">
              <span className="about-icon-circle">
                <Users size={26} />
              </span>
              <strong>People First</strong>
              <p>We build for people and the businesses that support them.</p>
            </div>

            <div className="value-card">
              <span className="about-icon-circle">
                <ShieldCheck size={26} />
              </span>
              <strong>Transparency</strong>
              <p>Clear systems over guesswork and confusion.</p>
            </div>

            <div className="value-card">
              <span className="about-icon-circle">
                <Zap size={26} />
              </span>
              <strong>Simplicity</strong>
              <p>Powerful features that are easy to use every day.</p>
            </div>

            <div className="value-card">
              <span className="about-icon-circle">
                <BarChart3 size={26} />
              </span>
              <strong>Efficiency</strong>
              <p>Less wasted effort. More time back for managers.</p>
            </div>

            <div className="value-card">
              <span className="about-icon-circle">
                <Heart size={26} />
              </span>
              <strong>Customer Success</strong>
              <p>We help teams work smoother every step of the way.</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="about-cta">
          <div>
            <h2>Let’s build stronger teams together.</h2>
            <p>
              Start your free trial of Oiiko and feel the clarity on day one.
            </p>
          </div>

          <button
            className="about-btn"
            onClick={() => {
              trackEvent("about_cta_click", {
                location: "about_page",
              });

              navigate("/register");
            }}
          >
            Get Started Free
          </button>
        </section>
      </main>
    </>
  );
};

export default AboutUs;
